import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BlogEditor } from "@/components/blog/BlogEditor";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { resolveBlogExcerpt } from "@/lib/blog-excerpt";
import { throwIfNotOk } from "@/lib/api-error";
import {
  CaretLeft,
  CircleNotch,
  FloppyDisk,
  PaperPlaneTilt,
  Image as ImageIcon,
  Eye,
  EyeSlash,
  Tag,
  UserCircle,
} from "@phosphor-icons/react";
import { BlogCategorySelect } from "@/components/blog/BlogCategorySelect";
import { TherapistCombobox } from "@/components/ui/therapist-combobox";

export const Route = createFileRoute("/dashboard/admin/blog/new")({
  component: AdminNewPost,
});

import { MediaPicker } from "@/components/ui/media-picker";
import { toast } from "@/lib/toast";

function AdminNewPost() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState<object | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [category, setCategory] = useState("");
  const [therapistId, setTherapistId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleContentChange = useCallback((newContent: object) => {
    setContent(newContent);
  }, []);

  const canSubmit = title.trim().length > 0 && content !== null && category !== "";

  const handleSubmit = async (status: string) => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/blog", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          content: JSON.stringify(content),
          excerpt: resolveBlogExcerpt({ content, title: title.trim() }),
          category,
          coverImageUrl: coverImageUrl || undefined,
          status,
          therapistId,
        }),
      });
      await throwIfNotOk(res, "Greška pri kreiranju članka.");
      await queryClient.invalidateQueries({ queryKey: ["blog"] });
      navigate({ to: "/dashboard/admin/blog" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Greška pri kreiranju članka.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Word count from content
  const wordCount = content ? countWords(content) : 0;

  return (
    <div className="mx-auto max-w-7xl">
      {/* Top bar */}
      <div className="mb-6 flex items-center justify-between sm:mb-8">
        <Link
          to="/dashboard/admin/blog"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <CaretLeft className="size-4" />
          Članci
        </Link>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden md:inline-flex"
            title={sidebarOpen ? "Sakrij panel" : "Prikaži panel"}
          >
            {sidebarOpen ? (
              <EyeSlash className="size-4" />
            ) : (
              <Eye className="size-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSubmit("draft")}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? (
              <CircleNotch className="size-3.5 animate-spin" />
            ) : (
              <FloppyDisk className="size-3.5" />
            )}
            Nacrt
          </Button>
          <Button
            size="sm"
            onClick={() => handleSubmit("published")}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? (
              <CircleNotch className="size-3.5 animate-spin" />
            ) : (
              <PaperPlaneTilt className="size-3.5" />
            )}
            Objavi
          </Button>
        </div>
      </div>

      <div className="flex gap-8">
        {/* Main editor area */}
        <div className="min-w-0 flex-1">
          {/* Title — large, editorial, borderless */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Naslov članka..."
            className="w-full border-none bg-transparent text-3xl font-bold tracking-tight text-foreground outline-none placeholder:text-muted-foreground/30 sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          />

          {/* Subtle metadata line */}
          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span>{wordCount} riječi</span>
            <span>~{Math.max(1, Math.ceil(wordCount / 200))} min čitanja</span>
            {!canSubmit && (
              <span className="text-amber-500">
                {!title.trim()
                  ? "Unesite naslov"
                  : !content
                    ? "Počnite pisati sadržaj"
                    : "Izaberite temu"}
              </span>
            )}
          </div>

          {/* Divider */}
          <div className="my-6 h-px bg-border" />

          {/* Editor */}
          <div className="[&_.rounded-lg]:rounded-none [&_.rounded-lg]:border-x-0 [&_.rounded-lg]:border-t-0 [&_.ProseMirror]:min-h-[500px] [&_.ProseMirror]:px-0">
            <BlogEditor
              initialContent={undefined}
              onChange={handleContentChange}
            />
          </div>
        </div>

        {/* Sidebar — metadata panel */}
        <div
          className={cn(
            "hidden shrink-0 transition-all duration-300 md:block",
            sidebarOpen ? "w-72 opacity-100" : "w-0 overflow-hidden opacity-0"
          )}
        >
          <div className="sticky top-6 space-y-5">
            {/* Category */}
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Tag className="size-3.5" />
                Tema
              </p>
              <BlogCategorySelect value={category} onChange={setCategory} />
            </div>

            {/* Linked therapist */}
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <UserCircle className="size-3.5" />
                Terapeut
              </p>
              <TherapistCombobox
                value={therapistId}
                onChange={setTherapistId}
                placeholder="Bez terapeuta"
              />
              <p className="text-xs leading-relaxed text-muted-foreground">
                Prikazuje se kao autor i članak izlazi na njegovom profilu.
              </p>
            </div>

            {/* Cover image */}
            <div className="space-y-2">
              <p
                className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                <ImageIcon className="size-3.5" />
                Naslovna slika
              </p>

              <MediaPicker
                value={coverImageUrl}
                onChange={setCoverImageUrl}
                folder="blog"
                hint="JPG, PNG ili WebP · maks. 5 MB"
                previewHeight="h-52 sm:h-64"
              />
            </div>

            {/* Info */}
            <div className="rounded-xl border bg-muted/20 p-3.5">
              <p className="text-xs leading-relaxed text-muted-foreground">
                <strong className="text-foreground">Nacrt</strong> sprema
                članak bez objavljivanja.{" "}
                <strong className="text-foreground">Objavi</strong> ga odmah
                stavlja na platformu.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar — shown below editor on small screens */}
      <div className="mt-8 space-y-5 md:hidden">
        <div className="h-px bg-border" />

        {/* Category mobile */}
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Tag className="size-3.5" />
            Tema
          </p>
          <BlogCategorySelect value={category} onChange={setCategory} />
        </div>

        {/* Linked therapist mobile */}
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <UserCircle className="size-3.5" />
            Terapeut
          </p>
          <TherapistCombobox
            value={therapistId}
            onChange={setTherapistId}
            placeholder="Bez terapeuta"
          />
          <p className="text-xs leading-relaxed text-muted-foreground">
            Prikazuje se kao autor i članak izlazi na njegovom profilu.
          </p>
        </div>

        {/* Cover image mobile */}
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <ImageIcon className="size-3.5" />
            Naslovna slika
          </p>

          <MediaPicker
            value={coverImageUrl}
            onChange={setCoverImageUrl}
            folder="blog"
            hint="JPG, PNG ili WebP · maks. 5 MB"
            previewHeight="h-52 sm:h-64"
          />
        </div>

        {/* Mobile action bar */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleSubmit("draft")}
            disabled={isSubmitting || !canSubmit}
            className="flex-1"
          >
            <FloppyDisk className="size-4" />
            Nacrt
          </Button>
          <Button
            onClick={() => handleSubmit("published")}
            disabled={isSubmitting || !canSubmit}
            className="flex-1"
          >
            <PaperPlaneTilt className="size-4" />
            Objavi
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Helpers ─── */

function countWords(content: object): number {
  try {
    const json = content as {
      content?: Array<{
        type?: string;
        content?: Array<{ text?: string; content?: Array<{ text?: string }> }>;
      }>;
    };
    let text = "";
    function walk(nodes: Array<{ text?: string; content?: unknown[] }>) {
      for (const node of nodes) {
        if (node.text) text += " " + node.text;
        if (Array.isArray(node.content)) walk(node.content as Array<{ text?: string; content?: unknown[] }>);
      }
    }
    if (json.content) walk(json.content as Array<{ text?: string; content?: unknown[] }>);
    return text
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
  } catch {
    return 0;
  }
}
